import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import { ValueInput } from '@/components/form/ValueInput.tsx';
import { DateInput } from '@/components/form/DateInput.tsx';
import { TextareaInput } from '@/components/form/TextareaInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import usePayExpense from '@/features/dashboard/expenses/hooks/usePayExpense.ts';
import useGetExpense from '@/features/dashboard/expenses/hooks/useGetExpense.ts';
import ExpenseFormSkeleton from '@/features/dashboard/expenses/components/ExpenseFormSkeleton.tsx';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import { getPossiblePaymentsMethods } from '@/features/dashboard/tourConfig/types/PaymentMethod.utils.ts';
import { remainingToPay } from '@/features/dashboard/expenses/types/TourExpenseDto.utils.ts';
import { formatPrice } from '@/utils/formatters.ts';

const payExpenseSchema = z.object({
  amount: z
    .number({ error: 'Kwota jest wymagana' })
    .positive('Kwota musi być większa niż 0'),
  paidAt: z.date({ error: 'Data zapłaty jest wymagana' }),
  paymentMethod: z.enum(PaymentMethod, {
    error: 'Sposób zapłaty jest wymagany',
  }),
  notes: z.string().default(''),
});

type PayExpenseFormData = z.infer<typeof payExpenseSchema>;
type PayExpenseInputData = z.input<typeof payExpenseSchema>;

interface PayExpenseModalProps {
  tourId: string;
  expenseId: string;
  onClose: () => void;
}

const PayExpenseModal = ({
  tourId,
  expenseId,
  onClose,
}: PayExpenseModalProps) => {
  const payMutation = usePayExpense(tourId);
  const { data: expense, isLoading } = useGetExpense(tourId, expenseId);

  const remaining = expense ? remainingToPay(expense) : 0;

  const methods = useForm<PayExpenseInputData>({
    resolver: zodResolver(payExpenseSchema),
    defaultValues: {
      paymentMethod: PaymentMethod.TRANSFER,
      notes: '',
    },
  });

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { errors },
  } = methods;

  // Kwotę znamy dopiero po doczytaniu wydatku, ale `values` tu nie przejdzie:
  // RHF porównuje je głęboko co render, a świeża `new Date()` nigdy nie równa
  // się poprzedniej — formularz resetowałby się w nieskończonej pętli.
  useEffect(() => {
    if (!expense) return;

    reset({
      amount: remaining > 0 ? remaining : undefined,
      paidAt: new Date(),
      paymentMethod: PaymentMethod.TRANSFER,
      notes: '',
    });
  }, [expense, remaining, reset]);

  const onSubmit = (data: PayExpenseFormData) => {
    if (!expense) return;

    payMutation.mutate(
      {
        expenseId: expense.id,
        request: {
          // Kwotę podaje się dodatnią — znak odwraca backend.
          amount: { amount: data.amount, currency: expense.amount.currency },
          paidAt: dayjs(data.paidAt).format('YYYY-MM-DD'),
          paymentMethod: data.paymentMethod,
          notes: data.notes || undefined,
        },
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader title="Zarejestruj zapłatę" onClose={onClose} />
          <ModalContent>
            {isLoading || !expense ? (
              <ExpenseFormSkeleton />
            ) : (
              <>
                <div className="rounded-md bg-neutral-50 p-3">
                  <InfoCardList className="flex-1 space-y-1">
                    <InfoCardRow layout="columns" label="Kwota faktury">
                      {formatPrice(
                        expense.amount.amount,
                        expense.amount.currency,
                      )}
                    </InfoCardRow>
                    <InfoCardRow layout="columns" label="Już zapłacono">
                      {formatPrice(expense.paidAmount, expense.amount.currency)}
                    </InfoCardRow>
                    <InfoCardRow layout="columns" label="Pozostało">
                      {formatPrice(remaining, expense.amount.currency)}
                    </InfoCardRow>
                  </InfoCardList>
                </div>

                <TwoInputsRow ratio="2/1">
                  <ValueInput
                    label="Kwota zapłaty"
                    register={register('amount', { valueAsNumber: true })}
                    placeholder="0"
                    required
                    type="number"
                    step={0.01}
                    error={errors.amount?.message}
                    sufix={expense.amount.currency}
                  />
                  <DateInput
                    dateFieldName="paidAt"
                    control={control}
                    label="Data zapłaty"
                    required
                    error={errors.paidAt?.message}
                  />
                </TwoInputsRow>

                <SingleSelectInput
                  fieldName="paymentMethod"
                  control={control}
                  options={getPossiblePaymentsMethods()}
                  label="Sposób zapłaty"
                  required
                  error={errors.paymentMethod?.message}
                />

                <TextareaInput
                  label="Notatka"
                  register={register('notes')}
                  error={errors.notes?.message}
                  placeholder="Opcjonalna notatka..."
                  rows={2}
                />
              </>
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={payMutation.isPending}
              disabled={isLoading || !expense}
            >
              Zarejestruj zapłatę
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default PayExpenseModal;
